import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { createEntityColRule, updateEntityColRule, listAllSchemaFields } from '../api/derivationConfig'
import type { EntityColRule, TransformRule } from '../types'

interface EntityColRuleModalProps {
  existing?: EntityColRule
  accId?: number
  onClose: () => void
}

const KEY_TYPES = ['entity', 'fhid'] as const
const COMMON_OPS = ['UPPERCASE', 'LOWERCASE', 'TRIM', 'ROUND', 'ABS', 'TO_STRING', 'TO_NUMBER']

function entityToColl(entity: string): string {
  if (entity === 'customer') return 'customer'
  if (entity === 'entity')   return 'system_transaction'
  return entity
}

export function EntityColRuleModal({ existing, accId, onClose }: EntityColRuleModalProps) {
  const queryClient = useQueryClient()
  const isEdit = !!existing

  const [formAccId, setFormAccId] = useState<string>(
    existing?.accId != null ? String(existing.accId) : accId != null ? String(accId) : '',
  )
  const [name, setName]           = useState(existing?.name ?? '')
  const [selectedEntity, setSelectedEntity] = useState<string>('')
  const [col, setCol]             = useState(existing?.col ?? '')
  const [coll, setColl]           = useState(existing?.coll ?? '')
  const [keyType, setKeyType]     = useState<'entity' | 'fhid'>(existing?.keyType ?? 'entity')
  const [xform, setXform]         = useState<TransformRule[]>(existing?.xform ?? [])
  const [feedback, setFeedback]   = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // ── Schema fields ─────────────────────────────────────────────────────────

  const { data: schemaFields = [] } = useQuery({
    queryKey: ['schema-all-fields'],
    queryFn: listAllSchemaFields,
    enabled: !isEdit,
  })

  // Distinct entity names for the first dropdown
  const entityTypes = [...new Set(schemaFields.map(f => f.entity))].sort()

  // Fields for the selected entity
  const fieldsForEntity = schemaFields
    .filter(f => f.entity === selectedEntity)
    .map(f => f.field)
    .sort()

  function handleEntitySelect(entity: string) {
    setSelectedEntity(entity)
    setCol('')
    setColl(entityToColl(entity))
  }

  function handleFieldSelect(field: string) {
    setCol(field)
  }

  // ── Mutation ──────────────────────────────────────────────────────────────

  const mutation = useMutation({
    mutationFn: (data: EntityColRule) =>
      isEdit
        ? updateEntityColRule(existing!.accId, existing!.name, data)
        : createEntityColRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-col-rules'] })
      setFeedback({ type: 'success', message: isEdit ? 'Template updated.' : 'Template created.' })
      setTimeout(onClose, 800)
    },
    onError: (err: Error) => {
      setFeedback({ type: 'error', message: err.message })
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const parsed = formAccId.trim() ? parseInt(formAccId, 10) : null
    mutation.mutate({
      accId: parsed,
      name: name.trim(),
      coll: coll.trim(),
      col: col.trim(),
      keyType,
      xform: xform.filter(x => x.op.trim()),
    })
  }

  // ── Transform helpers ─────────────────────────────────────────────────────

  function addXform() { setXform(prev => [...prev, { op: '', args: [] }]) }
  function removeXform(i: number) { setXform(prev => prev.filter((_, idx) => idx !== i)) }
  function setXformOp(i: number, op: string) {
    setXform(prev => prev.map((x, idx) => idx === i ? { ...x, op } : x))
  }
  function setXformArgs(i: number, raw: string) {
    const args = raw.split(',').map(s => s.trim()).filter(Boolean).map(s => {
      const n = Number(s); return isNaN(n) ? s : n
    })
    setXform(prev => prev.map((x, idx) => idx === i ? { ...x, args } : x))
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Entity Column Template' : 'New Entity Column Template'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-5">

            {/* Acc ID + Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Acc ID <span className="text-gray-400 font-normal">(blank = global)</span>
                </label>
                <Input
                  type="number"
                  placeholder="e.g. 3"
                  value={formAccId}
                  onChange={e => setFormAccId(e.target.value)}
                  disabled={isEdit}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-gray-400 font-normal text-xs">— logical ref key</span>
                </label>
                <Input
                  placeholder="e.g. txn.amount"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={isEdit}
                  required
                  className="font-mono"
                />
              </div>
            </div>

            {/* Storage Target */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Storage Target</p>

              {isEdit ? (
                /* Edit mode — read-only display */
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Field (dot-path)</label>
                    <div className="h-9 px-3 flex items-center rounded-md border border-gray-200 bg-gray-50 text-sm font-mono text-gray-700">
                      {col}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Collection prefix</label>
                    <div className="h-9 px-3 flex items-center rounded-md border border-gray-200 bg-gray-50 text-sm font-mono text-gray-700">
                      {coll}
                    </div>
                  </div>
                </div>
              ) : (
                /* Create mode — entity first, then field */
                <div className="space-y-3">
                  {/* Step 1 — Entity */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Entity</label>
                    <select
                      value={selectedEntity}
                      onChange={e => handleEntitySelect(e.target.value)}
                      className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      required
                    >
                      <option value="">— select entity —</option>
                      {entityTypes.map(e => (
                        <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2 — Field (shown once entity selected) */}
                  {selectedEntity && (
                    <div className="grid grid-cols-[1fr_140px] gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Field (dot-path)</label>
                        <select
                          value={col}
                          onChange={e => handleFieldSelect(e.target.value)}
                          className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                          required
                        >
                          <option value="">— select field —</option>
                          {fieldsForEntity.map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Key Type</label>
                        <select
                          value={keyType}
                          onChange={e => setKeyType(e.target.value as 'entity' | 'fhid')}
                          className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          {KEY_TYPES.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Derived coll hint */}
                  {selectedEntity && (
                    <p className="text-xs text-gray-400">
                      Collection prefix: <span className="font-mono text-gray-600">{coll}</span>
                      {' '}· Key type:{' '}
                      {keyType === 'fhid'
                        ? 'keyed by event fhid.'
                        : 'keyed by resolved entityKey attribute.'}
                    </p>
                  )}
                </div>
              )}

              {/* Key Type for edit mode */}
              {isEdit && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-500 mb-1">Key Type</label>
                  <select
                    value={keyType}
                    onChange={e => setKeyType(e.target.value as 'entity' | 'fhid')}
                    className="h-9 w-48 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {KEY_TYPES.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Transform pipeline */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Transform Pipeline{' '}
                  <span className="text-gray-400 font-normal text-xs">(optional, applied in order)</span>
                </label>
                <Button type="button" size="sm" variant="outline" onClick={addXform}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Op
                </Button>
              </div>
              <datalist id="xform-ops">
                {COMMON_OPS.map(op => <option key={op} value={op} />)}
              </datalist>
              {xform.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-1">No transforms — value stored as-is.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_32px] gap-2 text-xs font-medium text-gray-500 px-0.5">
                    <span>Operation</span><span>Args (comma-separated)</span><span />
                  </div>
                  {xform.map((x, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
                      <Input list="xform-ops" placeholder="e.g. UPPERCASE" value={x.op}
                        onChange={e => setXformOp(i, e.target.value)} className="font-mono text-sm" required />
                      <Input placeholder="e.g. 2" value={(x.args ?? []).join(', ')}
                        onChange={e => setXformArgs(i, e.target.value)} className="text-sm font-mono" />
                      <button type="button" onClick={() => removeXform(i)} className="text-gray-300 hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {feedback && (
              <div className={`text-sm px-4 py-2 rounded-lg ${
                feedback.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {feedback.message}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
